import { finishReviewsEqual, returnRequestsEqual } from './doctor-consult-view';
import { examinationEqual } from './clinical-examination-view';
/**
 * DOCTOR-ONE-CLOSE-AND-TRUST-V1 -- the consult TRUST LAYER.
 *
 * The founder E-SAVE finding (2026-07-09): typed clinical notes / a plan row
 * showed "Not recorded" after navigation. Root cause (run record 1(a),
 * verdict A + C): saves were click-ONLY (no autosave), and finalize sent
 * status ONLY -- it dropped unsaved sheet fields. This module is the fix:
 *
 *   - a debounced autosave that persists authored notes through the EXISTING
 *     draft path (createCaseSheet / updateCaseSheet -- NO new endpoint);
 *   - ONE visible save-state (Draft saved <time> / Saving... / Unsaved
 *     changes / Not saved) so the doctor always knows where their notes are;
 *   - a warn-on-navigate signal (dirty only) + a flush() the close act and
 *     the unmount path call so typed content is NEVER silently lost.
 *
 * The pure parts (the state model, the dirty detectors, the copy) are
 * React-free and unit-tested by direct call (repo idiom, no jsdom); the
 * timer/effect wiring lives in the useConsultAutosave hook.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { StructuredSheetForm } from '@/dashboard/doctor/doctor-consult-view';
import type { PlanItemEdit } from '@/dashboard/clinical-workbench/case-sheet-cockpit-view';
import type { ClinicalPlanItemView } from '@/dashboard/api';

// --- The single visible save state (TRUST LAW: ONE indicator) ---------------

/**
 * The save state the sheet renders. Exactly one line is ever shown:
 *   idle    -> nothing authored yet (nothing to say)
 *   clean   -> every authored edit is persisted ("Draft saved 1:45 PM")
 *   dirty   -> unsaved edits pending the debounce ("Unsaved changes")
 *   saving  -> a write is in flight ("Saving...")
 *   error   -> the last save failed; the edits are still in the form ("Not saved - ...")
 */
export type ConsultSaveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'clean'; readonly savedAt: string }
  | { readonly kind: 'dirty' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'error'; readonly message: string };

/** The ONE save-state line (null = render nothing, the idle state). */
export function consultSaveStateLabel(state: ConsultSaveState): string | null {
  switch (state.kind) {
    case 'idle':
      return null;
    case 'clean':
      return `Draft saved ${state.savedAt}`;
    case 'dirty':
      return 'Unsaved changes';
    case 'saving':
      return 'Saving...';
    case 'error':
      return `Not saved - ${state.message}`;
  }
}

/** The tone the indicator renders in (drives the design token / a11y colour). */
export function consultSaveStateTone(
  state: ConsultSaveState,
): 'positive' | 'pending' | 'muted' | 'danger' {
  switch (state.kind) {
    case 'clean':
      return 'positive';
    case 'saving':
      return 'pending';
    case 'error':
      return 'danger';
    case 'dirty':
    case 'idle':
      return 'muted';
  }
}

/**
 * WARN-ON-NAVIGATE fires ONLY when authored content is not safely persisted --
 * a pending debounce (dirty), an in-flight write (saving), or a failed save
 * (error). A clean/idle sheet never warns (the test pins "dirty only").
 */
export function shouldWarnOnNavigate(state: ConsultSaveState): boolean {
  return (
    state.kind === 'dirty' ||
    state.kind === 'saving' ||
    state.kind === 'error'
  );
}

/** The browser beforeunload backstop copy (shown by the browser on tab
 *  close / refresh / external navigation when the sheet is dirty). */
export const UNSAVED_NAV_WARNING =
  'You have unsaved consult notes. Leave without saving?';

/** The debounce window (ms) between the last keystroke and the autosave.
 *  Long enough to coalesce a burst of typing, short enough that the
 *  "unsaved" window is small. */
export const CONSULT_AUTOSAVE_DEBOUNCE_MS = 1200;

// --- Pure dirty detectors ----------------------------------------------------

/** True when the sheet form carries ANY non-blank authored content -- the
 *  gate for auto-CREATING a sheet (never auto-create an empty record). */
export function structuredFormHasContent(form: StructuredSheetForm): boolean {
  return (
    form.finishReview != null ||
    form.examination != null ||
    form.chiefComplaint.trim() !== '' ||
    form.findingsDiagnosis.trim() !== '' ||
    form.clinicalNote.trim() !== '' ||
    form.chiefComplaintCategory !== '' ||
    form.diagnosisCategory !== '' ||
    form.medicalDentalHistory.trim() !== '' ||
    form.examinationFindings.trim() !== '' ||
    form.followUpOption !== '' ||
    form.followUpNote.trim() !== '' ||
    form.toothRefs.length > 0
  );
}

/** Order-insensitive equality of two tooth selections (the write is a
 *  full-set replace; a reorder alone is not a change). Pure. */
function toothSelectionsEqual(
  a: readonly string[],
  b: readonly string[],
): boolean {
  if (a.length !== b.length) return false;
  const bs = new Set(b);
  return a.every((code) => bs.has(code));
}

/** Field-by-field equality of two sheet forms (dirty detection). Pure. */
export function structuredFormsEqual(
  a: StructuredSheetForm,
  b: StructuredSheetForm,
): boolean {
  return (
    finishReviewsEqual(a.finishReview,b.finishReview) &&
    returnRequestsEqual(a.returnRequest, b.returnRequest) &&
    examinationEqual(a.examination, b.examination) &&
    a.chiefComplaint === b.chiefComplaint &&
    a.findingsDiagnosis === b.findingsDiagnosis &&
    a.clinicalNote === b.clinicalNote &&
    a.chiefComplaintCategory === b.chiefComplaintCategory &&
    a.diagnosisCategory === b.diagnosisCategory &&
    a.medicalDentalHistory === b.medicalDentalHistory &&
    a.examinationFindings === b.examinationFindings &&
    a.followUpOption === b.followUpOption &&
    a.followUpNote === b.followUpNote &&
    // D2 (DOCTOR-FLOW-AFTER-INTAKE-V1): the treatment-doctor handoff is a
    // form edit like any other -- omitting it here left the form "clean"
    // after a handoff pick, so autosave never fired (walk-caught, W7).
    a.treatmentDoctorId === b.treatmentDoctorId &&
    toothSelectionsEqual(a.toothRefs, b.toothRefs)
  );
}

/** The plan-item ids whose inline edit differs from the persisted row
 *  (status, the decline reason on a declined row, or the doctor's per-line
 *  remark). Pure -- the close act flushes exactly these before locking, so no
 *  marked procedure and no typed remark is lost.
 *
 *  The remark compares TRIMMED, exactly as planItemEditPatch sends it: typing
 *  and then deleting back to blank over a row that had no remark leaves the
 *  row CLEAN, so a stray keystroke never raises a false unsaved-work warning
 *  or forces a pointless write. */
export function dirtyPlanItemIds(
  planItems: readonly ClinicalPlanItemView[],
  edits: Readonly<Record<number, PlanItemEdit>>,
): number[] {
  const out: number[] = [];
  for (const item of planItems) {
    const edit = edits[item.id];
    if (edit === undefined) continue;
    const persistedReason = item.declineReason ?? '';
    const editReason = edit.status === 'declined' ? edit.declineReason : '';
    const persistedNote = (item.itemNote ?? '').trim();
    const editNote = (edit.itemNote ?? '').trim();
    const noteChanged = edit.itemNote !== undefined && editNote !== persistedNote;
    if (
      edit.status !== item.status ||
      editReason !== persistedReason ||
      noteChanged
    ) {
      out.push(item.id);
    }
  }
  return out;
}

/** True when any authored-but-uncommitted work exists beyond the notes: a
 *  pending plan-item edit, an add-procedure form with a typed name, or a
 *  picked-but-unadded catalog selection (V6-P3). Feeds the aggregate
 *  warn/flush so the close act and navigate guard see ALL unsaved content,
 *  not just the notes. */
export function hasPendingPlanWork(
  planItems: readonly ClinicalPlanItemView[],
  edits: Readonly<Record<number, PlanItemEdit>>,
  addFormProcedureName: string,
  addFormCatalogSelected = false,
): boolean {
  return (
    dirtyPlanItemIds(planItems, edits).length > 0 ||
    addFormProcedureName.trim() !== '' ||
    addFormCatalogSelected
  );
}

// --- The debounced-autosave hook (timer wiring; the pure model above) --------

export interface ConsultAutosaveHook {
  /** The single visible save state for the notes indicator. */
  readonly saveState: ConsultSaveState;
  /** Call on every notes edit: (re)start the debounce and mark dirty. */
  readonly onEdit: () => void;
  /** Save now (if dirty / pending). Resolves when the write settles -- if a
   *  save is already in flight, JOINS it and awaits the result (never a false
   *  "could not save"). The close act and the unmount path await this so
   *  nothing is lost. Returns true when everything is persisted, false on a
   *  failed save (the caller surfaces it). */
  readonly flush: () => Promise<boolean>;
  /** Reset to idle (a fresh sheet load). */
  readonly reset: () => void;
}

/**
 * Debounced autosave for the consult notes. `save` performs the write from
 * the CURRENT form (create-or-update -- the caller closes over the latest
 * state via refs) and throws a mapped-message error on failure. `isDirty`
 * reports whether the live form differs from what was last persisted;
 * `hasContent` gates auto-create so an empty sheet is never created.
 *
 * Single-flight + trailing coalesce: only one write runs at a time; an edit
 * that lands during a write schedules exactly ONE more save after it, so a
 * burst never double-creates and never drops the last keystroke.
 */
export function useConsultAutosave(params: {
  readonly enabled: boolean;
  readonly debounceMs?: number;
  /** Manual clinical-profile edits share verified recovery, never autosave. */
  readonly automatic?: boolean;
  readonly isDirty: () => boolean;
  readonly hasContent: () => boolean;
  readonly formatNow: () => string;
  readonly save: () => Promise<void>;
}): ConsultAutosaveHook {
  const {
    enabled,
    debounceMs = CONSULT_AUTOSAVE_DEBOUNCE_MS,
    automatic = true,
    isDirty,
    hasContent,
    formatNow,
    save,
  } = params;

  const [saveState, setSaveState] = useState<ConsultSaveState>({ kind: 'idle' });

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The single in-flight save CYCLE (null = idle). flush() and the debounce
  // both JOIN this one promise, so a "Close" clicked mid-save AWAITS the notes
  // rather than aborting on a false "could not save" (adversarial-review fix).
  const inFlightRef = useRef<Promise<boolean> | null>(null);
  // Keep the latest callbacks without re-arming effects (stable identities).
  const saveRef = useRef(save);
  const isDirtyRef = useRef(isDirty);
  const hasContentRef = useRef(hasContent);
  const formatNowRef = useRef(formatNow);
  const enabledRef = useRef(enabled);
  const automaticRef = useRef(automatic);
  automaticRef.current = automatic;
  saveRef.current = save;
  isDirtyRef.current = isDirty;
  hasContentRef.current = hasContent;
  formatNowRef.current = formatNow;
  enabledRef.current = enabled;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /** The serialized save CYCLE: persist repeatedly until the form is clean or a
   *  write fails. Each pass reads the LIVEST form (the injected save reads its
   *  own refs), so a keystroke that lands mid-write is captured by the next
   *  pass (trailing coalesce) -- never a double-create, never a dropped last
   *  edit. */
  const saveCycle = useCallback(async (): Promise<boolean> => {
    if (!enabledRef.current || !isDirtyRef.current()) return true;
    while (isDirtyRef.current() && hasContentRef.current()) {
      setSaveState({ kind: 'saving' });
      try {
        await saveRef.current();
      } catch (err) {
        const message =
          err instanceof Error && err.message ? err.message : 'please retry';
        setSaveState({ kind: 'error', message });
        return false;
      }
    }
    if (isDirtyRef.current()) {
      // Dirty but not worth persisting (a fresh sheet blanked back to empty) --
      // leave it dirty rather than create an empty record.
      setSaveState({ kind: 'dirty' });
      return false;
    }
    setSaveState({ kind: 'clean', savedAt: formatNowRef.current() });
    return true;
  }, []);

  /** Start (or JOIN) the single in-flight save cycle. A caller during a running
   *  cycle awaits the SAME cycle -- which loops until the latest form is
   *  persisted -- so flush() (the close act) WAITS for the notes. */
  const runSave = useCallback((): Promise<boolean> => {
    if (inFlightRef.current !== null) return inFlightRef.current;
    const p = saveCycle().finally(() => {
      inFlightRef.current = null;
    });
    inFlightRef.current = p;
    return p;
  }, [saveCycle]);

  const onEdit = useCallback(() => {
    if (!enabledRef.current) return;
    setSaveState({ kind: 'dirty' });
    clearTimer();
    if (!automaticRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void runSave();
    }, debounceMs);
  }, [clearTimer, debounceMs, runSave]);

  const flush = useCallback((): Promise<boolean> => {
    clearTimer();
    return runSave();
  }, [clearTimer, runSave]);

  const reset = useCallback(() => {
    clearTimer();
    setSaveState({ kind: 'idle' });
  }, [clearTimer]);

  // Save-on-unmount: never leave the surface with typed content unsaved (the
  // in-app navigate backstop; the browser beforeunload covers tab-level nav).
  useEffect(() => {
    return () => {
      if (
        enabledRef.current && automaticRef.current &&
        isDirtyRef.current() &&
        hasContentRef.current() &&
        inFlightRef.current === null
      ) {
        // Best-effort flush on the way out; the write is idempotent.
        void saveRef.current().catch(() => undefined);
      }
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  return { saveState, onEdit, flush, reset };
}

/** Row-local edits join the consultation's existing deliberate save and verified
 * restore. This registers no writer: commit remains the line's audited PUT. */
export interface PlanControlDraft {
  dirty: () => boolean;
  flush: () => Promise<boolean>;
  prepareRestore: () => { valid: () => boolean; apply: () => void };
}
export interface PlanDraftOwner {
  register: (key: string, draft: PlanControlDraft) => () => void;
  changed: () => void;
}
export interface PlanControlDraftOptions {
  dirty: boolean; signature: string; pending: boolean;
  /** An exact-request owner can retry an uncertain result, never an active write. */
  retryPending?: boolean;
  commit: () => Promise<boolean | void> | boolean | void; reset: () => void;
}
export function createPlanControlDraft(read:()=>PlanControlDraftOptions):PlanControlDraft {
  return {
    dirty: () => read().dirty,
    flush: async () => {
      const current=read();
      return !current.dirty || ((!current.pending || current.retryPending===true) && await current.commit()===true);
    },
    prepareRestore: () => {
      const signature = read().signature;
      return { valid: () => signature === read().signature && !read().pending,
        apply: () => read().reset() };
    },
  };
}
export function usePlanControlDraft(owner: PlanDraftOwner | undefined, key: string, options:PlanControlDraftOptions): void {
  const latest = useRef(options); latest.current = options;
  const handle = useRef(createPlanControlDraft(()=>latest.current));
  useEffect(() => owner?.register(key, handle.current), [owner, key]);
  useEffect(() => { owner?.changed(); }, [owner, options.signature, options.dirty, options.pending]);
}
